import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  FaWhatsapp, FaSearch, FaCheckSquare, FaSquare, 
  FaUserPlus, FaTimes, FaPaperPlane, FaCalendarAlt, 
  FaMapMarkerAlt, FaUsers, FaUserCheck, FaPhoneAlt
} from 'react-icons/fa';

interface Member {
  id: number;
  name: string;
  number?: string | number;
  phone?: string | number;
  company_name?: string;
  business_classification?: string;
  designation?: string;
  profile?: string;
  status?: string;
}

interface EventData {
  id: number | string;
  heading?: string;
  title?: string;
  event_date?: string;
  start_date?: string;
  location?: string;
  venue?: string;
  presentation_1?: string;
  presentation_2?: string;
  amount?: string | number;
  dress_code?: string;
  meeting_meal?: string;
  image?: string;
  thumbnail?: string;
  photographs?: string;
  description?: string;
}

interface WhatsAppMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: EventData | null;
  onSuccess?: () => void;
}

const formatWaNumber = (phoneStr: any): string => {
  if (phoneStr === null || phoneStr === undefined) return '';
  let cleaned = String(phoneStr).replace(/\D/g, '');
  if (cleaned.length === 10) cleaned = '91' + cleaned;
  else if (cleaned.length === 11 && cleaned.startsWith('0')) cleaned = '91' + cleaned.slice(1);
  return cleaned;
};

const extractMeetingNum = (title: string): string => {
  if (!title) return '127th';
  const match = title.match(/(\d+(?:st|nd|rd|th)?)/i);
  if (match) {
    const val = match[1];
    if (/^\d+$/.test(val)) {
      const n = parseInt(val, 10);
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }
    return val;
  }
  return '127th';
};

const WhatsAppMeetingModal: React.FC<WhatsAppMeetingModalProps> = ({
  isOpen,
  onClose,
  event,
  onSuccess
}) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedNumbers, setSelectedNumbers] = useState<Set<string>>(new Set());
  
  // Custom additional phone numbers (admin 917301020313 pre-populated)
  const [customNumbers, setCustomNumbers] = useState<string[]>(['917301020313']);
  const [newCustomInput, setNewCustomInput] = useState<string>('');
  
  const [sending, setSending] = useState<boolean>(false);

  // Fetch all members roster on modal open
  useEffect(() => {
    if (!isOpen) return;
    const fetchMembers = async () => {
      setLoadingMembers(true);
      try {
        const res = await axios.get<any[]>('https://rmbbackend.rmbjaipur.com/auth/allusersinfo').catch(() => ({ data: [] }));
        const list: Member[] = Array.isArray(res.data) ? res.data : [];
        setMembers(list);

        // Pre-select all active members by default
        const activeNums = new Set<string>();
        list.forEach(m => {
          const num = formatWaNumber(m.number || m.phone || '');
          if (num && num.length >= 10) {
            activeNums.add(num);
          }
        });
        setSelectedNumbers(activeNums);
      } catch (err) {
        console.error('Error fetching member roster for WhatsApp campaign:', err);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [isOpen]);

  if (!isOpen || !event) return null;

  // Filtered members list
  const filteredMembers = members.filter(m => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const nameMatch = String(m.name || '').toLowerCase().includes(q);
    const compMatch = String(m.company_name || '').toLowerCase().includes(q);
    const numMatch = String(m.number || m.phone || '').includes(q);
    return nameMatch || compMatch || numMatch;
  });

  // Toggle single member
  const toggleMemberNumber = (num: string) => {
    const updated = new Set(selectedNumbers);
    if (updated.has(num)) {
      updated.delete(num);
    } else {
      updated.add(num);
    }
    setSelectedNumbers(updated);
  };

  // Select all filtered members
  const handleSelectAll = () => {
    const updated = new Set(selectedNumbers);
    filteredMembers.forEach(m => {
      const num = formatWaNumber(m.number || m.phone || '');
      if (num) updated.add(num);
    });
    setSelectedNumbers(updated);
  };

  // Deselect all filtered members
  const handleDeselectAll = () => {
    const updated = new Set(selectedNumbers);
    filteredMembers.forEach(m => {
      const num = formatWaNumber(m.number || m.phone || '');
      if (num) updated.delete(num);
    });
    setSelectedNumbers(updated);
  };

  // Add custom number
  const handleAddCustomNumber = (e: React.FormEvent) => {
    e.preventDefault();
    const formatted = formatWaNumber(newCustomInput.trim());
    if (!formatted || formatted.length < 10) {
      alert('Please enter a valid 10-digit or 12-digit mobile number.');
      return;
    }
    if (customNumbers.includes(formatted)) {
      alert('Number already added to custom recipients list.');
      return;
    }
    setCustomNumbers([...customNumbers, formatted]);
    setNewCustomInput('');
  };

  // Remove custom number
  const handleRemoveCustomNumber = (num: string) => {
    setCustomNumbers(customNumbers.filter(n => n !== num));
  };

  // Compute final combined list of recipients
  const allFinalNumbers = Array.from(new Set([
    ...Array.from(selectedNumbers),
    ...customNumbers
  ]));

  // Live Template Variables for Preview
  const eventTitle = event.heading || event.title || event.description || 'General Meeting';
  const meetingNumber = extractMeetingNum(eventTitle);
  const rawDateStr = event.event_date || event.start_date || new Date().toISOString();
  
  // Format clean date & time
  let displayDate = '26th September 2026 | Saturday';
  let displayTime = '8:00 AM onwards';
  try {
    const d = new Date(rawDateStr);
    if (!isNaN(d.getTime())) {
      const day = d.getDate();
      const s = ["th", "st", "nd", "rd"];
      const v = day % 100;
      const dayOrdinal = day + (s[(v - 20) % 10] || s[v] || s[0]);
      const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      displayDate = `${dayOrdinal} ${months[d.getMonth()]} ${d.getFullYear()} | ${days[d.getDay()]}`;
      let hours = d.getHours();
      const mins = d.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      displayTime = `${hours}:${mins < 10 ? '0' + mins : mins} ${ampm} onwards`;
    }
  } catch {}

  const venueStr = event.location || event.venue || 'The Fern , Tonk Road';
  const dressCode = event.dress_code || 'Formal';
  const mealStr = event.meeting_meal || 'Meeting followed by Breakfast';
  const scheduleLine = `📅 ${displayDate} | ⏰ ${displayTime} | 📍 Venue: ${venueStr} | 👔 Dress Code: ${dressCode} | 🍽️ ${mealStr}`;
  
  const pres1Str = event.presentation_1 || 'RMB Chapter Member Presentation';
  const pres2Str = event.presentation_2 || 'Education Slot by RMB Leader';
  const amountStr = String(event.amount || '1,200').replace(/^₹\s*/, '') || '1,200';

  // Header image resolution
  let headerImg = 'https://rmbjaipur.com/static/media/logo.f7e53f10137d7a7ffeb3.png';
  if (event.image) headerImg = event.image;
  else if (event.thumbnail) headerImg = event.thumbnail;
  else if (event.photographs) {
    try {
      const parsed = JSON.parse(event.photographs);
      if (Array.isArray(parsed) && parsed.length > 0) headerImg = parsed[0];
    } catch {
      headerImg = event.photographs;
    }
  }
  if (!headerImg.startsWith('http')) {
    headerImg = `https://rmbbackend.rmbjaipur.com/${headerImg.replace(/\\/g, '/').replace(/^\/+/, '')}`;
  }

  // Handle Dispatch API
  const handleSendCampaign = async () => {
    if (allFinalNumbers.length === 0) {
      alert('⚠️ Please select at least one recipient or add a phone number.');
      return;
    }

    const confirmMsg = `🚀 Send Official WhatsApp Meeting Template to ${allFinalNumbers.length} recipient(s)?\n\n` +
      `• Members selected: ${selectedNumbers.size}\n` +
      `• Additional numbers: ${customNumbers.length}\n` +
      `• Total Messages: ${allFinalNumbers.length}\n\n` +
      `Click OK to dispatch the campaign via GlobeBot Meta Cloud API.`;

    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    try {
      const rawId = String(event.id).replace('cal_', '').replace('news-', '');
      const res = await axios.post(`https://rmbbackend.rmbjaipur.com/auth/events/${rawId}/send-whatsapp`, {
        numbers: allFinalNumbers
      });

      if (res.data?.success) {
        alert(`🎉 WhatsApp meeting campaign dispatched successfully to ${allFinalNumbers.length} recipients!\n\nCampaign ID: ${res.data?.campaign?.campaign_id || 'Active'}`);
        if (onSuccess) onSuccess();
        onClose();
      } else {
        alert('Notice: ' + (res.data?.message || 'Failed to dispatch campaign'));
      }
    } catch (err: any) {
      console.error('Error dispatching campaign:', err);
      const msg = err.response?.data?.error || err.response?.data?.details || err.message;
      alert(`❌ Failed to dispatch campaign: ${msg}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5 overflow-y-auto animate-fadeIn">
      <div className="bg-white dark:bg-[#0D1526] w-full max-w-5xl rounded-3xl shadow-2xl border border-slate-200 dark:border-[#2A3B5C] flex flex-col max-h-[92vh] overflow-hidden">
        
        {/* Modal Top Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 via-teal-700 to-[#0A2451] text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-xl shadow-xs">
              <FaWhatsapp className="text-emerald-300" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black tracking-wide flex items-center gap-2">
                Broadcast WhatsApp Meeting Template
              </h2>
              <p className="text-xs text-emerald-100 font-medium truncate max-w-md">
                {eventTitle} • Variable Mode (Meta Verified Template: <span className="font-mono bg-black/20 px-1.5 py-0.5 rounded text-[11px]">meeting_temp</span>)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition cursor-pointer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Modal Body: Two-Column Responsive Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 flex-1 overflow-hidden">
          
          {/* LEFT COLUMN: Recipient Selection & Additional Numbers (7 Cols) */}
          <div className="lg:col-span-7 p-4 sm:p-5 flex flex-col overflow-hidden border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-[#1E2B45]">
            
            {/* Search & Bulk Selection Toolbar */}
            <div className="space-y-3 shrink-0 mb-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search member by name, company, or number..."
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-[#131E32] border border-slate-200 dark:border-[#2A3B5C] rounded-xl text-xs text-slate-900 dark:text-white font-medium outline-none focus:border-emerald-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-2.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-white"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Action Buttons & Counters */}
              <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900 text-emerald-700 dark:text-emerald-300 font-bold rounded-lg border border-emerald-300 dark:border-emerald-800 transition flex items-center gap-1.5 cursor-pointer text-xs"
                  >
                    <FaCheckSquare size={12} /> Select All ({filteredMembers.length})
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold rounded-lg border border-slate-300 dark:border-slate-700 transition flex items-center gap-1.5 cursor-pointer text-xs"
                  >
                    <FaSquare size={12} /> Deselect All
                  </button>
                </div>

                <div className="text-[11px] font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-[#1A2640] px-2.5 py-1 rounded-lg border border-slate-200 dark:border-[#2A3B5C]">
                  Selected: <strong className="text-emerald-600 dark:text-emerald-400">{selectedNumbers.size}</strong> / {members.length} members
                </div>
              </div>
            </div>

            {/* Member List Scrollable Roster */}
            <div className="flex-1 overflow-y-auto rounded-2xl border border-slate-200 dark:border-[#2A3B5C] bg-slate-50/50 dark:bg-[#131E32]/50 p-2 space-y-1.5 divide-y divide-slate-100 dark:divide-slate-800/60">
              {loadingMembers ? (
                <div className="py-12 text-center text-xs text-slate-400 flex flex-col items-center justify-center gap-2">
                  <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  Loading member directory...
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="py-12 text-center text-xs text-slate-400 font-semibold">
                  No members found matching "{searchQuery}"
                </div>
              ) : (
                filteredMembers.map((m) => {
                  const num = formatWaNumber(m.number || m.phone || '');
                  const isChecked = Boolean(num && selectedNumbers.has(num));
                  return (
                    <div
                      key={m.id}
                      onClick={() => num && toggleMemberNumber(num)}
                      className={`p-2.5 rounded-xl flex items-center justify-between gap-3 cursor-pointer transition select-none ${
                        isChecked
                          ? 'bg-emerald-500/10 border border-emerald-500/30 dark:bg-emerald-950/40 text-slate-900 dark:text-white'
                          : 'bg-white dark:bg-[#1A2640] hover:bg-slate-100 dark:hover:bg-slate-800/70 border border-transparent text-slate-600 dark:text-slate-400 opacity-75'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}} // handled by row click
                          className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 accent-emerald-600 cursor-pointer"
                        />
                        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-700 to-indigo-600 text-white font-bold flex items-center justify-center text-xs shrink-0 shadow-xs">
                          {m.name ? m.name.charAt(0).toUpperCase() : 'M'}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">
                            {m.name}
                          </div>
                          <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                            {m.company_name || 'RMB Jaipur Member'} {m.business_classification ? `• ${m.business_classification}` : ''}
                          </div>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <div className="text-[11px] font-mono font-bold text-slate-700 dark:text-slate-300">
                          {num ? `+${num}` : 'No phone'}
                        </div>
                        <span className={`text-[9px] font-extrabold px-1.5 py-0.2 rounded-full ${
                          isChecked ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300' : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                        }`}>
                          {isChecked ? 'Included' : 'Excluded'}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Custom Additional Phone Numbers Input Box */}
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-[#2A3B5C] shrink-0">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                <FaPhoneAlt size={11} className="text-emerald-500" />
                Additional WhatsApp Recipient Numbers (Custom / Testing)
              </label>
              
              <form onSubmit={handleAddCustomNumber} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newCustomInput}
                  onChange={(e) => setNewCustomInput(e.target.value)}
                  placeholder="Enter 10 or 12 digit number (e.g. 917301020313)..."
                  className="flex-1 px-3 py-1.5 bg-slate-50 dark:bg-[#131E32] border border-slate-200 dark:border-[#2A3B5C] rounded-xl text-xs text-slate-900 dark:text-white font-mono outline-none focus:border-emerald-500"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition cursor-pointer flex items-center gap-1 shadow-xs"
                >
                  <FaUserPlus size={11} /> Add
                </button>
              </form>

              {/* Tags of Added Custom Numbers */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {customNumbers.map(n => (
                  <span
                    key={n}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-100 dark:bg-emerald-950/70 border border-emerald-300 dark:border-emerald-800 text-[11px] font-mono font-bold text-emerald-900 dark:text-emerald-200 shadow-xs"
                  >
                    <span>+{n}</span>
                    {n === '917301020313' && <span className="text-[9px] uppercase font-black text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-950/80 px-1 rounded">Admin</span>}
                    <button
                      type="button"
                      onClick={() => handleRemoveCustomNumber(n)}
                      className="text-emerald-700 dark:text-emerald-300 hover:text-red-500 dark:hover:text-red-400 font-black ml-1 text-xs cursor-pointer"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN: WhatsApp Message Live Preview (5 Cols) */}
          <div className="lg:col-span-5 p-4 sm:p-5 bg-slate-100/70 dark:bg-[#0A111F] flex flex-col justify-between overflow-y-auto">
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black uppercase text-slate-500 dark:text-slate-400 tracking-wider flex items-center gap-1.5">
                  📱 WhatsApp Live Template Preview
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                  en_US • Meta Approved
                </span>
              </div>

              {/* Realistic WhatsApp Chat Bubble Container */}
              <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-300 dark:border-slate-800 bg-[#E5DDD5] dark:bg-[#0B141A]">
                {/* Simulated WhatsApp Chat Header */}
                <div className="bg-[#075E54] dark:bg-[#1F2C34] px-3.5 py-2.5 text-white flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs">
                    R
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold truncate">RMB Jaipur Official</div>
                    <div className="text-[9px] text-emerald-200">Verified Business Account</div>
                  </div>
                </div>

                {/* Simulated Chat Message Content */}
                <div className="p-3">
                  <div className="bg-white dark:bg-[#1F2C34] rounded-xl p-3 shadow-md border border-slate-200 dark:border-[#2A3B5C] space-y-2.5 text-xs text-slate-800 dark:text-slate-100">
                    
                    {/* Header Image */}
                    <div className="rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-800 aspect-video relative max-h-36">
                      <img
                        src={headerImg}
                        alt="Meeting Poster"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLElement).setAttribute('src', 'https://rmbjaipur.com/static/media/logo.f7e53f10137d7a7ffeb3.png');
                        }}
                      />
                      <span className="absolute bottom-1 right-1.5 bg-black/60 text-[9px] text-white px-1.5 py-0.5 rounded font-mono">
                        Header Component
                      </span>
                    </div>

                    {/* Template Body with Highlighted Variables */}
                    <div className="space-y-1.5 leading-relaxed text-[11px]">
                      <p className="font-extrabold text-slate-900 dark:text-white">
                        📢 RMB Jaipur – <span className="text-emerald-600 dark:text-emerald-400 font-black">{meetingNumber}</span> General Meeting | Final Call!
                      </p>

                      <div className="bg-slate-50 dark:bg-[#131E32] p-2 rounded-lg border border-slate-200 dark:border-[#2A3B5C] font-medium text-[10px] space-y-1">
                        <div>{scheduleLine}</div>
                      </div>

                      <div className="space-y-1 text-[10px]">
                        <div>
                          💼 <strong className="text-slate-700 dark:text-slate-300">Presentation 1:</strong>{' '}
                          <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{pres1Str}</span>
                        </div>
                        <div>
                          🎓 <strong className="text-slate-700 dark:text-slate-300">Education Slot:</strong>{' '}
                          <span className="text-purple-600 dark:text-purple-400 font-semibold">{pres2Str}</span>
                        </div>
                        <div>
                          💰 <strong className="text-slate-700 dark:text-slate-300">Meeting Fee:</strong>{' '}
                          <span className="text-emerald-600 dark:text-emerald-400 font-bold">₹{amountStr}</span>
                        </div>
                      </div>
                    </div>

                    {/* Chat Bubble Footer */}
                    <div className="flex items-center justify-end gap-1 text-[9px] text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-800">
                      <span>Just now</span>
                      <span className="text-blue-500 font-bold">✓✓</span>
                    </div>

                  </div>
                </div>
              </div>

              {/* Delivery Stats Box */}
              <div className="mt-4 p-3 bg-white dark:bg-[#131E32] rounded-2xl border border-slate-200 dark:border-[#2A3B5C] space-y-2">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                  <span>Total Recipients Selected:</span>
                  <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                    {allFinalNumbers.length} recipients
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400 space-y-0.5">
                  <div className="flex justify-between">
                    <span>Active Chapter Members:</span>
                    <strong className="text-slate-700 dark:text-slate-300">{selectedNumbers.size}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Custom / Admin Numbers:</span>
                    <strong className="text-slate-700 dark:text-slate-300">{customNumbers.length}</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Bottom Action Controls */}
            <div className="mt-5 pt-3 border-t border-slate-200 dark:border-[#2A3B5C] flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSendCampaign}
                disabled={sending || allFinalNumbers.length === 0}
                className={`px-5 py-2.5 rounded-xl text-xs font-black text-white flex items-center gap-2 shadow-lg transition cursor-pointer ${
                  sending || allFinalNumbers.length === 0
                    ? 'bg-slate-400 cursor-not-allowed opacity-60'
                    : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 active:scale-95 shadow-emerald-500/20'
                }`}
              >
                {sending ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Broadcasting...</span>
                  </>
                ) : (
                  <>
                    <FaPaperPlane size={11} />
                    <span>Send to {allFinalNumbers.length} Recipient(s)</span>
                  </>
                )}
              </button>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default WhatsAppMeetingModal;
